import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  TextField,
  Grid,
} from "@mui/material";

/**
 * Dialog for selecting a contact to link as the registered agent.
 */
export default function SelectContactForRegisteredAgentDialog({
  open,
  onClose,
  organization = null,
  contacts = [],
  selectedContact = null,
  onSelectedContactChange,
  renewalDate = "",
  renewalDateMonth = "",
  renewalDateDay = "",
  onRenewalDateChange,
  onRenewalDateMonthChange,
  onRenewalDateDayChange,
  onConfirm,
}) {
  const linkedContact =
    organization?.registered_agent && contacts.find((c) => c.id === organization.registered_agent);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Contact for Registered Agent</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {/* Current Registered Agent Summary */}
        {organization && (organization.registered_agent || organization.registered_agent_name) && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              bgcolor: "background.default",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Current Registered Agent:
            </Typography>
            {linkedContact ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Linked Contact:</strong>{" "}
                  {`${linkedContact.first_name || ""} ${linkedContact.last_name || ""}`.trim() ||
                    linkedContact.email ||
                    "Unnamed Contact"}
                </Typography>
                {linkedContact.email && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {linkedContact.email}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                  gap: 1.5,
                }}
              >
                {organization.registered_agent_name && (
                  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    <strong>Name:</strong> {organization.registered_agent_name}
                  </Typography>
                )}
                {organization.registered_agent_type && (
                  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    <strong>Type:</strong> {organization.registered_agent_type}
                  </Typography>
                )}
                {organization.registered_agent_street && (
                  <Typography
                    variant="body2"
                    sx={{ gridColumn: { xs: "1", sm: "1 / -1" }, wordBreak: "break-word" }}
                  >
                    <strong>Address:</strong> {organization.registered_agent_street}
                  </Typography>
                )}
                {organization.registered_agent_city && (
                  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    <strong>City:</strong> {organization.registered_agent_city}
                  </Typography>
                )}
                {organization.registered_agent_state && (
                  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    <strong>State:</strong> {organization.registered_agent_state}
                  </Typography>
                )}
                {organization.registered_agent_zip && (
                  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    <strong>ZIP:</strong> {organization.registered_agent_zip}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Warning Message */}
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            bgcolor: "warning.light",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "warning.main",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
            ⚠️ Important:
          </Typography>
          <Typography variant="body2">
            Selecting a contact will link the registered agent to this contact and remove any
            manually entered registered agent fields (name, address, etc.). The contact's
            information will be used instead.
          </Typography>
        </Box>

        <FormControl fullWidth margin="dense">
          <InputLabel id="select-contact-label">Contact</InputLabel>
          <Select
            labelId="select-contact-label"
            label="Contact"
            value={selectedContact?.id || ""}
            onChange={(e) => {
              const contactId = e.target.value;
              const contact = contacts.find((c) => c.id === contactId);
              onSelectedContactChange(contact || null);
            }}
          >
            <MenuItem value="">None</MenuItem>
            {contacts.map((contact) => (
              <MenuItem key={contact.id} value={contact.id}>
                <ListItemText
                  primary={
                    `${contact.first_name || ""} ${contact.last_name || ""}`.trim() ||
                    contact.email ||
                    "Unnamed Contact"
                  }
                  secondary={contact.email || contact.phone || ""}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {contacts.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            No contacts available for this organization.
          </Typography>
        )}

        {/* Registered Agent Renewal Date Fields */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Registered Agent Renewal Date (Optional):
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Renewal Date (registered_agent_renewal_date)"
                type="date"
                value={renewalDate}
                onChange={(e) => onRenewalDateChange(e.target.value)}
                margin="dense"
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Renewal Date Month (registered_agent_renewal_date_month)"
                value={renewalDateMonth}
                onChange={(e) => onRenewalDateMonthChange(e.target.value)}
                margin="dense"
                size="small"
                placeholder="e.g., January"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Renewal Date Day (registered_agent_renewal_date_day)"
                value={renewalDateDay}
                onChange={(e) => onRenewalDateDayChange(e.target.value)}
                margin="dense"
                size="small"
                placeholder="e.g., 15"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => onConfirm(selectedContact)}
          disabled={!selectedContact}
        >
          Select
        </Button>
      </DialogActions>
    </Dialog>
  );
}
